import { Award, Copy, History, Loader2, Pencil, Plus, Sparkles, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ROUTES } from '../../constants/routes';
import { useCompanySports } from '../../hooks/useCompanySports';
import { useBeltProfiles } from '../../hooks/belts/useBelts';
import { useCloneBeltProfile } from '../../hooks/belts/useBeltMutations';
import type { BeltProfile } from '../../types/belts';

/**
 * Belt profile management entry point (#746).
 *
 * Two sections:
 *   1. System templates — read-only library every gym sees. Clicking "Clone"
 *      opens a dialog to pick a discipline (company_sports) and confirm name.
 *   2. Company profiles — this gym's cloned profiles. Clicking opens the
 *      editor for renaming, rank management, etc.
 */
export default function BeltProfilesList() {
  const profiles = useBeltProfiles();
  const [cloneSource, setCloneSource] = useState<BeltProfile | null>(null);

  const { system, company } = useMemo(() => {
    const list = profiles.data ?? [];
    return {
      system: list.filter((p) => p.is_system),
      company: list.filter((p) => !p.is_system),
    };
  }, [profiles.data]);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#EBFFEE] flex items-center gap-2">
            <Award className="w-6 h-6" />
            Belts &amp; Ranks
          </h1>
          <p className="text-sm text-white/60 mt-1">
            Browse the template library, clone a profile into your gym, and tune
            rank thresholds per discipline.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to={ROUTES.BELT_HISTORY}
            className="inline-flex items-center gap-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white/80 px-3 py-1.5 text-sm font-medium border border-white/10"
          >
            <History className="w-4 h-4" /> History
          </Link>
          <Link
            to={ROUTES.BELT_PROMOTIONS}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 px-3 py-1.5 text-sm font-medium border border-emerald-500/30"
          >
            <Sparkles className="w-4 h-4" /> Promotions
          </Link>
        </div>
      </div>

      {profiles.isLoading && (
        <div className="flex items-center gap-2 text-white/60">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading profiles…
        </div>
      )}

      {profiles.isError && (
        <div className="rounded-md bg-red-500/10 border border-red-500/30 text-red-300 p-3 text-sm">
          Couldn’t load profiles: {(profiles.error as Error).message}
        </div>
      )}

      {!profiles.isLoading && (
        <>
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#EBFFEE]">
              Your gym’s profiles
            </h2>
            {company.length === 0 ? (
              <p className="text-sm text-white/50">
                No company profiles yet. Clone a template below to get started.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {company.map((p) => (
                  <CompanyProfileCard key={p.id} profile={p} />
                ))}
              </div>
            )}
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-[#EBFFEE]">
              System templates
            </h2>
            <p className="text-xs text-white/50">
              Read-only. Clone one to create an editable copy bound to a
              discipline your gym teaches.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {system.map((p) => (
                <SystemTemplateCard
                  key={p.id}
                  profile={p}
                  onClone={() => setCloneSource(p)}
                />
              ))}
            </div>
          </section>
        </>
      )}

      {cloneSource && (
        <CloneDialog
          source={cloneSource}
          onClose={() => setCloneSource(null)}
        />
      )}
    </div>
  );
}

function CompanyProfileCard({ profile }: { profile: BeltProfile }) {
  const editPath = ROUTES.BELT_PROFILE_EDIT.replace(':profileId', profile.id);
  return (
    <Link
      to={editPath}
      className="group rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20 p-4 transition flex flex-col gap-2"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="font-medium text-[#EBFFEE] line-clamp-2">
          {profile.name}
        </span>
        <Pencil className="w-4 h-4 text-white/40 group-hover:text-white/70 shrink-0 mt-0.5" />
      </div>
      {profile.description && (
        <p className="text-xs text-white/60 line-clamp-2">{profile.description}</p>
      )}
    </Link>
  );
}

function SystemTemplateCard({
  profile,
  onClone,
}: {
  profile: BeltProfile;
  onClone: () => void;
}) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 flex flex-col gap-3">
      <div>
        <div className="font-medium text-[#EBFFEE]">{profile.name}</div>
        {profile.description && (
          <p className="text-xs text-white/60 mt-1 line-clamp-3">
            {profile.description}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onClone}
        className="inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 px-3 py-1.5 text-sm font-medium border border-emerald-500/30 transition"
      >
        <Copy className="w-4 h-4" /> Clone to my gym
      </button>
    </div>
  );
}

function CloneDialog({
  source,
  onClose,
}: {
  source: BeltProfile;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const sports = useCompanySports();
  const clone = useCloneBeltProfile();

  const [sportId, setSportId] = useState<string>('');
  const [name, setName] = useState<string>(source.name);
  const [error, setError] = useState<string | null>(null);

  // Preselect the only sport if the gym teaches just one — common case.
  React.useEffect(() => {
    if (!sportId && sports.data && sports.data.length === 1) {
      setSportId(sports.data[0].id);
    }
  }, [sports.data, sportId]);

  const submit = async () => {
    setError(null);
    if (!sportId) {
      setError('Pick a discipline this profile applies to.');
      return;
    }
    if (!name.trim()) {
      setError('Name can’t be empty.');
      return;
    }
    try {
      const created = await clone.mutateAsync({
        sourceProfileId: source.id,
        sportId,
        name: name.trim(),
        description: source.description,
      });
      onClose();
      navigate(ROUTES.BELT_PROFILE_EDIT.replace(':profileId', created.id));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#0f1612] border border-white/10 rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-[#EBFFEE]">
              Clone &ldquo;{source.name}&rdquo;
            </h3>
            <p className="text-xs text-white/60 mt-1">
              Creates an editable copy bound to one of your gym’s disciplines.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/50 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wide text-white/50">
            Profile name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-[#EBFFEE] focus:outline-none focus:border-emerald-500/50"
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wide text-white/50">
            Discipline
          </label>
          {sports.isLoading ? (
            <div className="text-sm text-white/50">Loading disciplines…</div>
          ) : (sports.data ?? []).length === 0 ? (
            <div className="text-sm text-amber-300">
              Your gym doesn’t have any sports configured yet. Add one in
              settings first.
            </div>
          ) : (
            <select
              value={sportId}
              onChange={(e) => setSportId(e.target.value)}
              className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-[#EBFFEE] focus:outline-none focus:border-emerald-500/50"
            >
              <option value="">Pick a discipline…</option>
              {(sports.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.sport_name}
                </option>
              ))}
            </select>
          )}
        </div>

        {error && (
          <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-md p-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-white/70 hover:text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={clone.isPending || !sportId || !name.trim()}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-black px-3 py-1.5 text-sm font-medium"
          >
            {clone.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            <Plus className="w-4 h-4" /> Create
          </button>
        </div>
      </div>
    </div>
  );
}
